import {
  ButtonContainer,
  CreditPointsContainer,
  CreditPointsNumber,
  ViewCreditButton,
} from 'components/credits/credit-items/CreditPointsSection/components';

import CaptionText from 'components/common/CaptionText';
import { CreditType } from 'types/credit';
import FlexContainer from 'components/common/FlexContainer';
import { Fragment } from 'react';
import HeartIconContainer from 'components/common/HeartIconContainer';
import { Link } from 'react-router-dom';
import { Spin } from 'antd';
import useCreditBookmarks from 'hooks/use-credit-bookmarks';
import useIsMobile from 'hooks/use-is-mobile';
import useMetadata from 'hooks/use-metadata';
import useTranslation from 'hooks/use-translation';

type Props = {
  creditItemID: string;
  creditType: CreditType;
  courseName?: string;
  creditPoints: number;
};

export function CreditPointsSection(props: Props): JSX.Element {
  const { creditItemID, creditType, creditPoints, courseName } = props;
  const isMobile = useIsMobile();
  const translation = useTranslation();
  const metadata = useMetadata();
  const { creditBookmarks, addCreditBookmark, removeCreditBookmark } = useCreditBookmarks();

  if (!translation || !metadata) {
    return <Spin />;
  }

  const onBookmarkClick = () => {
    if (existsInBookmarks) {
      removeCreditBookmark(creditItemID);
      return;
    }
    addCreditBookmark({ creditItemID, creditType, timestamp: metadata.timestamp });
  };

  const path = creditType === CreditType.ARTICULATION ? 'articulations' : 'precedents';

  const existsInBookmarks = creditBookmarks.has(creditItemID);

  return (
    <ButtonContainer className='credit-points-section' gap={16}>
      <FlexContainer
        className='credit-points-section-top-row'
        gap={32}
        flexDirection='row'
        justifyContent={isMobile ? 'flex-start' : 'flex-end'}
      >
        <CreditPointsContainer gap={4}>
          {creditPoints > 0 ? (
            <div role='text' aria-label={`${creditPoints} credit points`}>
              <CreditPointsNumber className='credit-points'>{creditPoints}</CreditPointsNumber>
              <CaptionText className='credit-points-label'>Credit points</CaptionText>
            </div>
          ) : (
            <Fragment>
              <span className='gauranteed-entry-only-label'>Guaranteed</span>
              <span className='gauranteed-entry-only-label'>entry only</span>
            </Fragment>
          )}
        </CreditPointsContainer>
        {!isMobile && <HeartIconContainer selected={existsInBookmarks} onSelect={onBookmarkClick} />}
      </FlexContainer>

      <Link
        to={`/details/${path}/${creditItemID}`}
        aria-label={courseName ? `View credits for ${courseName}` : `View credit details for ${creditPoints} points`}
      >
        <ViewCreditButton className='credit-details-button' type='primary'>
          <div>{translation.common.buttons.viewCredits}</div>
        </ViewCreditButton>
      </Link>
    </ButtonContainer>
  );
}
